import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { QuestionMarkCircleIcon, LifebuoyIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';

const CATEGORIES = [
    { value: 'business_interaction', label: 'Business interaction help → Program Manager' },
    { value: 'technical', label: 'Technical help → Trainer / Product Specialist' },
    { value: 'mentor_support', label: 'Mentor support → Mentor' },
    { value: 'uncomfortable_interaction', label: 'Uncomfortable interaction → Program Manager' },
    { value: 'other', label: 'Other → Program Manager' }
];

export default function HelpSupport() {
    const [faq, setFaq] = useState([]);
    const [mine, setMine] = useState([]);
    const [form, setForm] = useState({
        category: 'other',
        subject: '',
        description: '',
        attachment: null
    });
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            const [faqRes, mineRes] = await Promise.all([
                axios.get('/student/help/faq'),
                axios.get('/student/support')
            ]);
            setFaq(faqRes.data.data || []);
            setMine(mineRes.data.data || []);
        } catch (err) {
            toast.error('Unable to load help center');
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            if (form.attachment) {
                const formData = new FormData();
                formData.append('category', form.category);
                formData.append('subject', form.subject);
                formData.append('description', form.description);
                formData.append('attachment', form.attachment);
                await axios.post('/student/support', formData);
            } else {
                await axios.post('/student/support', {
                    category: form.category,
                    subject: form.subject,
                    description: form.description
                });
            }
            toast.success('Support request submitted');
            setForm({ category: 'other', subject: '', description: '', attachment: null });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not submit request');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="w-full space-y-8">
            <PageHeader
                title="Help & Support"
                description="Guidance for your internship command center — hours, learning, business work, and escalation when you need people involved."
            />

            <section className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-sm text-amber-950">
                <strong>Confidentiality:</strong> Never upload customer data, passwords, private
                financial records, or confidential material without authorization. Describe problems
                in general terms when escalating.
            </section>

            <section className="space-y-4">
                <h2 className="text-lg font-medium flex items-center gap-2">
                    <QuestionMarkCircleIcon className="h-5 w-5" />
                    Common questions
                </h2>
                {faq.map((section) => (
                    <div key={section.section} className="card p-4">
                        <h3 className="font-medium text-ink-950">{section.section}</h3>
                        <ul className="mt-3 space-y-3">
                            {(section.items || []).map((item) => (
                                <li key={item.q}>
                                    <p className="text-sm font-medium text-ink-800">{item.q}</p>
                                    <p className="text-sm text-ink-600 mt-0.5">{item.a}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                <div className="text-sm text-ink-600 space-y-1">
                    <p>
                        Mentor questions → <Link className="text-brand-700 underline" to="/my-mentor">My Mentor</Link>
                    </p>
                    <p>
                        Thinking partner → <Link className="text-brand-700 underline" to="/ai-coach">AI Coach</Link>
                    </p>
                </div>
            </section>

            <section className="card p-5 space-y-4">
                <h2 className="text-lg font-medium flex items-center gap-2">
                    <LifebuoyIcon className="h-5 w-5" />
                    Support / escalation request
                </h2>
                <form onSubmit={onSubmit} className="space-y-3">
                    <select
                        className="input-field"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                    <input
                        className="input-field"
                        placeholder="Subject"
                        required
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    />
                    <textarea
                        className="input-field min-h-[120px]"
                        required
                        placeholder="Describe the issue without confidential attachments or credentials."
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <div>
                        <label className="block text-sm font-medium text-ink-700 mb-1">
                            Add Attachment (optional)
                        </label>
                        <input
                            type="file"
                            onChange={(e) => setForm({ ...form, attachment: e.target.files[0] || null })}
                            className="block w-full text-sm text-ink-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 cursor-pointer"
                        />
                        {form.attachment && (
                            <p className="mt-1 text-xs text-brand-700 font-medium">Selected: {form.attachment.name}</p>
                        )}
                    </div>
                    <button type="submit" className="btn-primary" disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Submit request'}
                    </button>
                </form>
            </section>

            {mine.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-lg font-medium">Your requests</h2>
                    <ul className="space-y-2">
                        {mine.map((r) => (
                            <li
                                key={r.id}
                                className="rounded-lg border border-border bg-white p-3 text-sm"
                            >
                                <p className="font-medium text-ink-950">{r.subject}</p>
                                <p className="text-xs text-ink-500 mt-1">
                                    {r.category} · {r.status} · routed to {r.routed_to}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
